// ====================================================
// src/components/block/liveStreamManagement/videoEffects/AvatarProceduralModels.ts
//
// Accessoires générés en géométrie Three.js pure — pas de fichier .glb à charger ni
// de licence à vérifier, pour des formes assez simples pour ne pas le justifier (cf.
// AvatarModels.ts, champ `build`). Chaque fonction retourne un THREE.Object3D prêt à
// être centré/ancré par AvatarRenderer.instantiate() — aucune position ne dépend
// d'un repère du visage ici, seulement de la forme elle-même.
// ====================================================

import * as THREE from 'three';

/** Couronne : anneau + pointes — bâtie base à y=0 (cf. anchor: 'aboveHead' dans
 * AvatarModels.ts, qui ancre au bas du modèle, pas à son centre). */
export function buildCrown(): THREE.Object3D {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0xfacc15, roughness: 0.3, metalness: 0.7 });

  const band = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.08, 12, 32), material);
  band.rotation.x = Math.PI / 2;
  band.position.y = 0.08;
  group.add(band);

  const spikeCount = 5;
  for (let i = 0; i < spikeCount; i++) {
    const angle = (i / spikeCount) * Math.PI * 2;
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.4, 8), material);
    spike.position.set(Math.cos(angle) * 0.5, 0.08 + 0.2, Math.sin(angle) * 0.5);
    group.add(spike);
  }

  const jewelMaterial = new THREE.MeshStandardMaterial({ color: 0xef4444, roughness: 0.2, metalness: 0.1 });
  const jewel = new THREE.Mesh(new THREE.OctahedronGeometry(0.1), jewelMaterial);
  jewel.position.set(0, 0.08 + 0.42, 0.5);
  group.add(jewel);

  return group;
}

/** Bague : anneau + pierre — centrée sur son propre milieu (cf. anchor: 'hand' dans
 * AvatarModels.ts, qui ancre au centre de la main, pas au bas). */
export function buildRing(): THREE.Object3D {
  const group = new THREE.Group();
  const bandMaterial = new THREE.MeshStandardMaterial({ color: 0xfacc15, roughness: 0.25, metalness: 0.8 });
  const band = new THREE.Mesh(new THREE.TorusGeometry(0.35, 0.09, 12, 32), bandMaterial);
  // THREE.TorusGeometry est à plat dans le plan XY par défaut (l'axe du "trou" est
  // Z, face à la caméra) — pour qu'un doigt puisse PASSER DEDANS, il faut que ce
  // trou soit aligné avec l'axe du doigt (Y local, cf. updateHandSlotTransform :
  // le roulis tourne le modèle autour de Z pour aligner Y sur poignet→majeur).
  // Sans ce quart de tour, l'anneau se présentait de face, tourné à 90° par
  // rapport au doigt (constaté en régie réelle) au lieu de l'entourer.
  band.rotation.x = Math.PI / 2;
  group.add(band);

  const jewelMaterial = new THREE.MeshStandardMaterial({ color: 0x38bdf8, roughness: 0.1, metalness: 0.1 });
  const jewel = new THREE.Mesh(new THREE.OctahedronGeometry(0.16), jewelMaterial);
  jewel.position.z = 0.35;
  group.add(jewel);

  return group;
}

/** Lunettes : deux verres + un pont — centrées sur leur propre milieu (cf. anchor:
 * 'eyes' dans AvatarModels.ts, qui ancre au centre, pas au bas). */
export function buildGlasses(): THREE.Object3D {
  const group = new THREE.Group();
  const frame = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.3, metalness: 0.4 });

  const lensGeo = new THREE.TorusGeometry(0.35, 0.05, 12, 24);
  const left  = new THREE.Mesh(lensGeo, frame);
  left.position.x = -0.42;
  const right = new THREE.Mesh(lensGeo, frame);
  right.position.x = 0.42;
  group.add(left, right);

  const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.05, 0.05), frame);
  group.add(bridge);

  // Branches — leur grande dimension est sur Z (vers l'ARRIÈRE, perpendiculaire au
  // plan des verres), pas sur X : de vraies branches partent du charnière (bord
  // extérieur du verre) et filent vers l'arrière pour se poser sur les oreilles,
  // elles ne s'étalent pas sur le côté du visage (ce que faisait l'ancienne
  // géométrie, longue en X — elle rentrait dans le visage plutôt que de le
  // contourner, constaté). Légère ouverture vers l'extérieur en s'éloignant du
  // visage (rotation.y), comme de vraies branches qui s'évasent un peu.
  const armGeo = new THREE.BoxGeometry(0.04, 0.04, 0.5);
  const armLeft  = new THREE.Mesh(armGeo, frame);
  armLeft.position.set(-0.72, 0, -0.25);
  armLeft.rotation.y = Math.PI / 10;
  const armRight = new THREE.Mesh(armGeo, frame);
  armRight.position.set(0.72, 0, -0.25);
  armRight.rotation.y = -Math.PI / 10;
  group.add(armLeft, armRight);

  return group;
}

/** Moustache "guidon" : deux boucles symétriques + un pont central — centrée sur son
 * propre milieu (cf. anchor: 'face' + offset vers le bas dans AvatarModels.ts, pour
 * tomber au niveau de la lèvre supérieure plutôt qu'entre les sourcils). */
export function buildMoustache(): THREE.Object3D {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0x2b1a12, roughness: 0.6, metalness: 0.05 });

  // Un côté construit, l'autre est son miroir (scale.x = -1) — même arc de tore
  // partagé, pas besoin de dupliquer la géométrie.
  const curlGeo = new THREE.TorusGeometry(0.32, 0.07, 10, 20, Math.PI * 1.15);
  const left = new THREE.Mesh(curlGeo, material);
  left.rotation.z = Math.PI * 0.15;
  left.position.set(-0.3, 0, 0);
  const right = new THREE.Mesh(curlGeo, material);
  right.rotation.z = Math.PI * 0.15;
  right.scale.x = -1;
  right.position.set(0.3, 0, 0);
  group.add(left, right);

  // Pont central sous le nez, reliant les deux boucles.
  const bridge = new THREE.Mesh(new THREE.CapsuleGeometry(0.08, 0.2, 4, 8), material);
  bridge.rotation.z = Math.PI / 2;
  group.add(bridge);

  return group;
}

/** Perruque : calotte (dôme élargi, déborde un peu sous l'équateur pour couvrir les
 * oreilles) + frange devant + deux mèches qui tombent sur les côtés — bâtie base au
 * point le plus bas (cf. anchor: 'aboveHead' dans AvatarModels.ts, même ancrage que
 * la Couronne : le bas du modèle, pas son centre, se pose au point d'ancrage). Pas
 * un rendu de mèches individuelles (hors de portée en primitives Three.js), une
 * silhouette stylisée qui se lit comme "des cheveux" à la taille d'une vignette régie. */
export function buildWig(): THREE.Object3D {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0x3b2417, roughness: 0.85, metalness: 0 });

  // Calotte : demi-sphère élargie (X/Z) qui déborde un peu au-delà de l'équateur
  // (thetaLength > π/2) pour tomber légèrement sur les côtés/oreilles plutôt que de
  // s'arrêter pile au sommet du crâne.
  const cap = new THREE.Mesh(new THREE.SphereGeometry(0.6, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.62), material);
  cap.scale.set(1.18, 1, 1.08);
  group.add(cap);

  // Frange sur le devant — arc de tore qui déborde légèrement devant la calotte.
  const fringe = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.12, 8, 16, Math.PI * 0.9), material);
  fringe.rotation.x = Math.PI / 2;
  fringe.rotation.z = Math.PI * 1.25; // centre l'arc vers l'avant (+Z)
  fringe.position.y = -0.06;
  group.add(fringe);

  // Deux mèches qui tombent le long des joues, de chaque côté — assez COURTES pour
  // ne pas descendre beaucoup plus bas que la calotte elle-même (cf. plus haut,
  // le pivot 'aboveHead' — cf. AvatarModels.ts — prend le point le plus BAS de
  // TOUT le modèle : des mèches trop longues faisaient flotter toute la perruque
  // bien au-dessus de la tête, son propre "bas" étant en fait le bout des mèches,
  // pas la lisière de la calotte — constaté en régie réelle, "perruque au-dessus
  // de la couronne, pas posée sur la tête").
  const lockGeo = new THREE.CapsuleGeometry(0.08, 0.3, 4, 8);
  const lockLeft = new THREE.Mesh(lockGeo, material);
  lockLeft.position.set(-0.58, -0.05, 0.2);
  lockLeft.rotation.z = 0.15;
  const lockRight = new THREE.Mesh(lockGeo, material);
  lockRight.position.set(0.58, -0.05, 0.2);
  lockRight.rotation.z = -0.15;
  group.add(lockLeft, lockRight);

  return group;
}

/** Tête de référence GÉNÉRIQUE pour le panneau de calibration d'une décoration (cf.
 * DecorationCalibrationModal) — PAS un modèle utilisable en régie, juste un repère
 * visuel pour positionner une décoration 'face'/'eyes'/'aboveHead' relativement à un
 * visage. Construite pour que la distance entre les deux repères "œil" ci-dessous
 * vaille EXACTEMENT 1 unité — la même unité que DecorationTransform.position
 * (multiples de la distance inter-yeux), pour que le réglage visuel se transpose
 * directement en régie réelle. */
export function buildReferenceHead(): THREE.Object3D {
  const group = new THREE.Group();
  const skin = new THREE.MeshStandardMaterial({ color: 0xe8b98a, roughness: 0.75 });
  const dark = new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.5 });

  const head = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 18), skin);
  head.scale.set(0.85, 1.05, 0.95); // proportions grossières d'une tête humaine
  group.add(head);

  // Repères "yeux", écartés d'exactement 1 unité, à hauteur du centre de la tête —
  // cohérent avec computeAnchorPoint('eyes') dans AvatarRenderer.ts (milieu des
  // deux yeux) et 'face' (assez proche, entre les sourcils, juste au-dessus).
  const eyeGeo = new THREE.SphereGeometry(0.06, 10, 8);
  const eyeLeft  = new THREE.Mesh(eyeGeo, dark); eyeLeft.position.set(-0.5, 0, 0.85);
  const eyeRight = new THREE.Mesh(eyeGeo, dark); eyeRight.position.set(0.5, 0, 0.85);
  group.add(eyeLeft, eyeRight);

  // Repère "menton" — juste un point de repère visuel supplémentaire (bas du visage).
  const chin = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), dark);
  chin.position.set(0, -0.95, 0.7);
  group.add(chin);

  return group;
}

/** Main de référence GÉNÉRIQUE, même principe que buildReferenceHead — largeur de
 * paume (base index ↔ base auriculaire) fixée à EXACTEMENT 1 unité, main ouverte à
 * plat, doigts vers le haut (même convention que updateHandSlotTransform : roulis 0
 * = main dressée doigts vers le haut). */
export function buildReferenceHand(): THREE.Object3D {
  const group = new THREE.Group();
  const skin = new THREE.MeshStandardMaterial({ color: 0xe8b98a, roughness: 0.75 });

  const palm = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 0.25), skin);
  palm.position.y = 0.5;
  group.add(palm);

  const fingerGeo = new THREE.CapsuleGeometry(0.09, 0.7, 4, 8);
  for (const x of [-0.4, -0.15, 0.12, 0.4]) { // index → auriculaire
    const finger = new THREE.Mesh(fingerGeo, skin);
    finger.position.set(x, 1.35, 0);
    group.add(finger);
  }
  const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.45, 4, 8), skin);
  thumb.position.set(-0.65, 0.25, 0.15);
  thumb.rotation.z = Math.PI / 3;
  group.add(thumb);

  const wrist = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.5, 0.6, 12), skin);
  wrist.position.y = -0.3;
  group.add(wrist);

  return group;
}

/** Un "os" du rig (cf. buildHandRigParts) : le segment entre deux repères MediaPipe
 * consécutifs (ex. 5→6 = phalange proximale de l'index) — cf. HAND_RIG_BONES. */
export interface HandRigBone {
  startIdx: number;
  endIdx: number;
  /** Géométrie "unité" (rayon 1, hauteur 1, axe Y, centrée) — redimensionnée/
   * réorientée CHAQUE frame par AvatarRenderer.updateHandRigTransform (jamais
   * reconstruite). Cylindre (styles 'stick'/'skeleton') ou capsule ('skin'). */
  mesh: THREE.Mesh;
  /** Rayon RELATIF de CET os (paume plus large, phalanges qui s'affinent vers le
   * bout du doigt) — cf. boneRadiusFactor. AvatarRenderer multiplie le rayon de
   * base (mesuré depuis la largeur de paume réelle) par ce facteur. */
  radiusFactor: number;
}

/** UNE articulation du rig — un des 21 repères MediaPipe (poignet + 4 par doigt). */
export interface HandRigJoint {
  index: number;
  /** Sphère "unité" (rayon 1, centrée) — repositionnée chaque frame. */
  mesh: THREE.Mesh;
}

export interface HandRigParts {
  group: THREE.Group;
  bones: HandRigBone[];
  joints: HandRigJoint[];
  /** Partagé par TOUS les os/articulations de CE rig — sa couleur est mise à jour
   * une fois par frame selon hand.handedness (cf. AvatarRenderer), sans avoir à
   * toucher les dizaines de maillages individuellement. Basic (styles 'stick'/
   * 'skeleton', aplat) ou Standard ('skin', éclairé) — les deux exposent `.color`,
   * contrairement au type THREE.Material générique. */
  material: THREE.MeshBasicMaterial | THREE.MeshStandardMaterial;
  /** Rayon des articulations, en multiples du rayon de base des os — cf. AvatarModels
   * 'skeleton' (nœuds noueux plus gros) vs 'skin' (transitions lisses, articulations
   * à peine plus grosses que les os) vs 'stick' (valeur d'origine). */
  jointRadiusRatio: number;
}

export type HandRigStyle = 'stick' | 'skeleton' | 'skin';

/** Paires (repère de départ, repère d'arrivée) de chaque "os" du rig — indices
 * MediaPipe HandLandmarker standard (21 points) : 0=poignet, puis par doigt
 * (CMC/MCP, PIP, DIP, TIP) : pouce 1-4, index 5-8, majeur 9-12, annulaire 13-16,
 * auriculaire 17-20. Les 5 premières paires forment la paume (poignet → base de
 * chaque doigt) ; les 15 suivantes, les 3 phalanges de chaque doigt. */
export const HAND_RIG_BONES: [number, number][] = [
  [0, 1], [0, 5], [0, 9], [0, 13], [0, 17],       // paume
  [1, 2], [2, 3], [3, 4],                          // pouce
  [5, 6], [6, 7], [7, 8],                          // index
  [9, 10], [10, 11], [11, 12],                     // majeur
  [13, 14], [14, 15], [15, 16],                    // annulaire
  [17, 18], [18, 19], [19, 20],                    // auriculaire
];
export const HAND_RIG_JOINT_COUNT = 21;
export const HAND_RIG_WRIST_INDEX = 0;

/** Rayon relatif d'un os selon sa position dans HAND_RIG_BONES (son "ordre") : les 5
 * premiers (paume) sont les plus épais — plusieurs capsules qui se recouvrent près
 * du poignet, une façon simple de suggérer une paume pleine sans polygone dédié —
 * puis chaque doigt s'affine de sa base (proximale) vers son bout (distale). */
function boneRadiusFactor(order: number): number {
  if (order < 5) return 1.15; // paume
  const withinFinger = (order - 5) % 3;
  return withinFinger === 0 ? 1 : withinFinger === 1 ? 0.82 : 0.62;
}

const STYLE_BASE_COLOR: Record<HandRigStyle, number> = {
  stick:    0xf0c8a0, // teinte de peau — remplacée de toute façon par la couleur gauche/droite
  skeleton: 0xf1ede4, // ivoire/os
  skin:     0xf0c8a0, // teinte de peau
};
const STYLE_JOINT_RADIUS_RATIO: Record<HandRigStyle, number> = {
  stick:    1.2,
  skeleton: 1.7, // nœuds noueux bien marqués, façon articulations osseuses
  skin:     1.05, // transitions lisses, presque pas de "boule" visible à l'articulation
};
const STYLE_BONE_RADIUS_SCALE: Record<HandRigStyle, number> = {
  stick:    1,
  skeleton: 0.7, // os plus fins que les deux autres styles, pour contraster avec les grosses articulations
  skin:     1,
};

/** Main articulée — une sphère par articulation MediaPipe (21) reliées par des "os"
 * (20), repositionnés/réorientés CHAQUE frame directement depuis les repères réels
 * de la main suivie (cf. AvatarRenderer.updateHandRigTransform) plutôt qu'un seul
 * bloc rigide : chaque phalange bouge indépendamment, comme les doigts réels.
 * Entièrement procédural (aucun fichier), une seule matière partagée par rig
 * (teintée ensuite selon main gauche/droite détectée). Trois styles, cf. AvatarModels
 * (même mécanisme de suivi pour les trois, seule l'apparence change) :
 *   - 'stick'    : cylindres fins, look "boules et bâtonnets" d'origine.
 *   - 'skeleton' : os plus fins mais articulations bien plus grosses (nœuds), teinte
 *                  ivoire — un peu comme 'stick', en plus "osseux".
 *   - 'skin'     : capsules arrondies, épaisseur dégressive base→bout du doigt
 *                  (paume suggérée par les 5 capsules de paume qui se recouvrent),
 *                  matière éclairée (pas juste un aplat) pour un peu de relief.
 * Géométries "unité" : le redimensionnement précis (longueur d'os, rayon selon la
 * largeur de paume mesurée) se fait par AvatarRenderer, jamais ici. */
export function buildHandRigParts(style: HandRigStyle): HandRigParts {
  const group = new THREE.Group();
  const color = STYLE_BASE_COLOR[style];
  const material: THREE.MeshBasicMaterial | THREE.MeshStandardMaterial = style === 'skin'
    ? new THREE.MeshStandardMaterial({ color, roughness: 0.65, metalness: 0.05 })
    : new THREE.MeshBasicMaterial({ color });

  const jointGeometry = new THREE.SphereGeometry(1, 10, 7);
  const boneRadiusScale = STYLE_BONE_RADIUS_SCALE[style];

  const bones: HandRigBone[] = HAND_RIG_BONES.map(([startIdx, endIdx], order) => {
    const radiusFactor = boneRadiusFactor(order) * boneRadiusScale;
    // 'skin' : capsules arrondies (plus organique) — chaque os a SA PROPRE géométrie
    // (le rayon "unité" varie déjà d'un os à l'autre, cf. radiusFactor ci-dessus,
    // rebaké directement dans la géométrie plutôt que dans l'échelle par frame).
    // 'stick'/'skeleton' : cylindres, partagent tous la même géométrie de base —
    // moins organique mais suffisant pour ces deux styles, et un peu plus léger.
    const geometry = style === 'skin'
      ? new THREE.CapsuleGeometry(radiusFactor, 1, 4, 8)
      : new THREE.CylinderGeometry(radiusFactor, radiusFactor, 1, 8);
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);
    // Rayon déjà dans la géométrie (ci-dessus) pour 'skin' → facteur neutre ici pour
    // ne pas l'appliquer une seconde fois ; 'stick'/'skeleton' partagent une géométrie
    // à rayon 1, donc le facteur doit lui être appliqué à l'échelle (cf. AvatarRenderer).
    return { startIdx, endIdx, mesh, radiusFactor: style === 'skin' ? 1 : radiusFactor };
  });

  const joints: HandRigJoint[] = [];
  for (let index = 0; index < HAND_RIG_JOINT_COUNT; index++) {
    const mesh = new THREE.Mesh(jointGeometry, material);
    group.add(mesh);
    joints.push({ index, mesh });
  }

  return { group, bones, joints, material, jointRadiusRatio: STYLE_JOINT_RADIUS_RATIO[style] };
}
